import moment from "moment";

export const ADHOC_TABLE_PREFIX = "Adhoc";
export const ADHOC_TABLES_STORAGE_KEY = "adhoc-tables";
export const ADHOC_TABLE_CLEAR_TIME = 10;

/**
 * Filters out free/inactive adhoc tables that have had no activity for more than ADHOC_TABLE_CLEAR_TIME (10 mins).
 */
export function filterStaleTables(tables: any[], clearTimeMinutes = ADHOC_TABLE_CLEAR_TIME): any[] {
  const currentTime = moment();

  return (tables || []).filter((table: any) => {
    if (table?.currentStatus === "Occupied") return true;

    if (!table?.lastUpdated) return false;

    const duration = moment.duration(currentTime.diff(moment(table?.lastUpdated)));

    return duration.asMinutes() < clearTimeMinutes;
  });
}

/**
 * Generates the default name for a new adhoc table.
 *
 * Example:
 * getDefaultAdhocTableName(0) -> "Table 1"
 * getDefaultAdhocTableName(2) -> "Table 3"
 */
export function getDefaultAdhocTableName(adhocTableCount: number): string {
  return `Table ${adhocTableCount + 1}`;
}

/**
 * Adds the Adhoc prefix to the table name.
 *
 * Example:
 * getAdhocTableName("Table 1") -> "Adhoc Table 1"
 */
export function getAdhocTableName(name: string): string {
  return `${ADHOC_TABLE_PREFIX} ${name.trim()}`;
}

/**
 * Checks whether the adhoc table name is valid.
 */
export function isValidAdhocTableName(name: string): boolean {
  return !!name?.trim();
}

/**
 * Checks whether an adhoc table with the same name already exists.
 */
export function isDuplicateAdhocTableName(tables: any[], name: string): boolean {
  const fullName = getAdhocTableName(name);

  return tables.some((table: any) => table?.name === fullName);
}

/**
 * Creates the common adhoc table object.
 *
 * Storage and Redux handling are intentionally not included here
 * because those are platform-specific.
 */
export function createAdhocTableData(name: string, guestCount: number): any {
  return {
    capacity: guestCount || 1,
    currentStatus: "Free",
    name: getAdhocTableName(name),
    orders: [],
    lastUpdated: moment().toISOString(),
  };
}

/**
 * Checks whether a sale belongs to an adhoc table.
 */
function normaliseAdhocTableName(value: string = ''): string {
  return (value || '').trim().replace(/\s+/g, ' ').toLowerCase();
}

export function isAdhocSale(saleData: any): boolean {
  const serviceLabel = saleData?.serviceLabel || {};
  const label = normaliseAdhocTableName(serviceLabel?.label || '');

  if (!saleData || saleData?.code) return false;

  if (serviceLabel?.isAdhoc === true) return true;

  return label.startsWith('adhoc ');
}

/**
 * Finds the index of the adhoc table matching the sale's service label.
 */
export function getAdhocTableIndex(adhocTables: any[] = [], saleData: any): number {
  const serviceLabel = saleData?.serviceLabel || {};
  const targetLabel = normaliseAdhocTableName(serviceLabel?.label || '');
  const targetResourceName = normaliseAdhocTableName(serviceLabel?.resourceName || '');

  return (adhocTables || []).findIndex((table: any) => {
    const tableName = normaliseAdhocTableName(table?.name || '');
    const strippedTableName = tableName.replace(/^adhoc\s+/, '');
    const strippedTarget = targetLabel.replace(/^adhoc\s+/, '');

    return (
      tableName === targetLabel ||
      tableName === targetResourceName ||
      strippedTableName === strippedTarget ||
      tableName === `adhoc ${strippedTarget}` ||
      targetLabel === `adhoc ${strippedTableName}`
    );
  });
}

